import { Copy } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import type { LogEntry } from "@/mocks/logs";
import { formatDateTime } from "@/pages/admin/data";

interface PayloadModalProps {
  entry: LogEntry | null;
  onClose: () => void;
  onViewMandate: (entry: LogEntry) => void;
}

export default function PayloadModal({ entry, onClose, onViewMandate }: PayloadModalProps) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  if (!entry) return null;

  const json = JSON.stringify(entry.payload, null, 2);

  function copyPayload() {
    navigator.clipboard
      .writeText(json)
      .then(() => {
        setCopied(true);
        toast.success("Payload copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => toast.error("Could not copy payload"));
  }

  const meta = [
    { label: "Timestamp", value: formatDateTime(entry.timestamp) },
    { label: "Ref No", value: entry.refNo },
    { label: "Retailer", value: `${entry.shopName} (${entry.retailerId})` },
    { label: "Event", value: entry.event },
    { label: "Source", value: entry.source },
    { label: "IP Address", value: entry.ipAddress },
    { label: "Performed By", value: entry.performedBy },
  ];

  const isMandate =
    Boolean(entry.refNo && entry.refNo.startsWith("EM")) ||
    Boolean(entry.event && entry.event.toLowerCase().includes("mandate")) ||
    Boolean(entry.payload?.mandateId) ||
    Boolean(entry.payload?.refNo && String(entry.payload.refNo).startsWith("EM"));

  return (
    <Modal
      open
      onClose={onClose}
      title="Event Details"
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {isMandate && (
            <Button onClick={() => onViewMandate(entry)}>View Mandate</Button>
          )}
        </div>
      }
    >
      <p className="text-[13px] text-ink-secondary">{entry.refNo}</p>
      <dl className="mt-4 flex flex-col">
        {meta.map((row, i) => (
          <div
            key={row.label}
            className={`flex items-center justify-between gap-4 py-2.5 ${i > 0 ? "border-t border-[#F1F5F9]" : ""}`}
          >
            <dt className="shrink-0 text-[13px] text-ink-secondary">{row.label}</dt>
            <dd className="min-w-0 text-right text-sm font-medium text-ink">{row.value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-4">
        <p className="mb-2 text-[13px] font-medium text-ink-secondary">Raw Payload</p>
        <div className="relative">
          <div className="max-h-[320px] overflow-y-auto rounded-lg border border-[#E2E8F0] bg-page p-3">
            <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-ink">{json}</pre>
          </div>
          <button
            type="button"
            onClick={copyPayload}
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-white text-ink-muted shadow-sm hover:text-ink cursor-pointer"
            aria-label="Copy payload"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </Modal>
  );
}